
import React, { useState } from "react";
import { Order, Product, OrderItem } from "../../interfaces/orders_manage";

import { availableIcons } from "../../utils/icons";
import { InvoicePrint } from "./InvoicePrint";
import ReactDOMServer from "react-dom/server";

// Create icon map
const iconMap = new Map<string, React.ComponentType<any>>(
  availableIcons.map(icon => [icon.name, icon.component as React.ComponentType<any>])
);

// Icon rendering function
const renderIcon = (iconName?: string | null) => {
  if (!iconName) return <span className="subnavbar__icon">📂</span>;
  const Icon = iconMap.get(iconName);
  return Icon ? <Icon className="subnavbar__icon" /> : <span className="subnavbar__icon">📂</span>;
};


// Status color helper
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'delivered': return 'status-delivered';
    case 'shipped': return 'status-shipped';
    case 'processing': return 'status-processing';
    case 'cancelled': return 'status-cancelled';
    case 'pending': return 'status-pending';
    default: return 'status-default';
  }
};

// Date formatter
const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// ORDER CARD COMPONENT
interface OrderCardProps {
  order: EnrichedOrder;
  navigate: (path: string) => void;
}

interface EnrichedOrder extends Order {
  items: (OrderItem & {
    product?: Product;
  })[];
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, navigate }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleDetails = () => setExpanded(!expanded);

  const generateInvoice = (order: EnrichedOrder) => {

    const html = ReactDOMServer.renderToString(
      <InvoicePrint order={order} />
    );

    const win = window.open("", "_blank");

    win?.document.write(`
    <html>
      <head>
        <title>Invoice</title>

        <style>
         body {
  margin: 0;
  padding: 40px;
  background: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
}



.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.invoice-title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
}

.invoice-sub {
  margin-top: 4px;
  color: #666;
  font-size: 14px;
}

.invoice-meta {
  text-align: right;
  font-size: 14px;
}

.invoice-meta p {
  margin: 2px 0;
}

.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 25px 0;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;
}

.invoice-table th {
  text-align: left;
  font-size: 13px;
  color: #555;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 10px;
}

.invoice-table td {
  padding: 14px 0;
  border-bottom: 1px solid #f1f1f1;
}

.invoice-table .desc {
  font-weight: 500;
}

.invoice-table .amount {
  font-weight: 600;
}

.totals-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

.totals-card {
  width: 260px;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.totals-divider {
  height: 1px;
  background: #ddd;
  margin: 10px 0;
}

.totals-row.grand {
  font-size: 18px;
  font-weight: bold;
}

        </style>

      </head>
      <body>
        ${html}
      </body>
    </html>
  `);

    win?.document.close();
    win?.print();
  };




  return (
    <div className={`order-card ${expanded ? 'expanded' : ''}`}>
      <div className="order-summary" onClick={toggleDetails}>
        <div className="order-meta">
          <div className="order-id">
            {renderIcon('FiShoppingBag')}
            ORDER #{order.id.slice(0, 8).toUpperCase()}
          </div>
          <div className="order-date">
            {renderIcon('FiCalendar')}
            {formatDate(order.createdAt)}
          </div>
        </div>

        <div className="order-info">
          <div className="order-stats">
            <span className="item-count">{order.items.length} item(s)</span>
            <span className="amount">
              {renderIcon('FiDollarSign')}
              ₹{order.totalAmount.toFixed(2)}
            </span>
            <button
              className="invoice-btn"
              onClick={(e) => {
                e.stopPropagation();
                generateInvoice(order);
              }}
            >
              🧾 Invoice
            </button>

          </div>
          <div className={`status ${getStatusColor(order.status)}`}>
            {order.status}
            <span className={`status-indicator ${getStatusColor(order.status)}`}></span>
          </div>
          <div className="toggle-icon">
            {expanded ? renderIcon('FiChevronUp') : renderIcon('FiChevronDown')}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="order-details">
          <div className="details-section">
            <h3>Order Details</h3>
            <div className="detail-row">
              <span className="detail-label">Order ID:</span>
              <span className="detail-value">{order.id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Order Date:</span>
              <span className="detail-value">{formatDate(order.createdAt)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className={`detail-value status ${getStatusColor(order.status)}`}>
                {order.status}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Address</span>
              <span className="detail-value">{order.addressId}</span>
            </div>
          </div>

          <div className="items-section">
            <h3>Order Items</h3>
            <div className="items-grid">
              {order.items.map((item) => (
                <div key={item.id} className="order-item">
                  <div className="item-image-container">
                    {item.product?.images?.[0] ? (
                      <img
                        src={item.product.images[0]}
                        alt={item.productName}
                        className="item-image"
                        onClick={() => navigate(`/product/${item.productId}`)}
                      />
                    ) : (
                      <div className="image-placeholder">
                        {renderIcon('FiPackage')}
                      </div>
                    )}
                  </div>
                  <div className="item-info">
                    <h4
                      className="item-name"
                      onClick={() => navigate(`/product/${item.productId}`)}
                    >
                      {item.productName}
                    </h4>
                    {item.vendorId && (
                      <div className="item-vendor">Vendor: {item.vendorId}</div>
                    )}
                    <div className="item-meta">
                      <div className="quantity">Quantity: {item.quantity}</div>
                      <div className="price">₹{item.price.toFixed(2)}</div>
                    </div>
                    <div className="item-total">
                      Item Total: <span>₹{(item.quantity * item.price).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="order-summary-section">
            <h3>Order Summary</h3>
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>₹{order.totalAmount.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping:</span>
              <span>Free</span>
            </div>
            <div className="summary-row">
              <span>Tax:</span>
              <span>₹0.00</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>₹{order.totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};